/* eslint-disable max-len */
import { Model } from 'app/model/backendmodel';
import { DataFlowExpressionUtils } from 'app/model/adapters/dataflow/expression.util';
import { CompletionAction, DataFlowSource, IsolationLevel, SQLSourceOptions } from 'app/model/dataflow/dataflow-source';
import { FLOW, IDataFlowSource } from 'app/model/adapters/dataflow/IDataflow';
import { ReferenceAdapter } from 'app/model/adapters/reference.adapter';
import { Metamodel } from 'app/model/base/model';
import { Dataset } from 'app/model/dataset/dataset';
import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { DataFlowSchemaAdapterUtils } from './dataflow-schema-adapter-utils';
import {
    IDslScriptable,
    TransformationAdapter
} from 'app/model/adapters/dataflow/transformations/base-transformation.adapter';
import { GlobalVariables } from 'app/services/globals';
import {
    AzureDataExplorerDataset,
    AzureSqlDWTableDataset,
    BaseAsyncAdapter,
    DataWarehouseLinkedService,
    DelimitedTextDataset,
    ITridentResource,
    JsonDataset,
    LakehouseLinkedService,
    ParquetDataset,
    ReadBehavior,
    SapODataDataset,
    SelfHostedIntegrationRuntime,
    SnowflakeTableDataset
} from 'shell-models-lib';
import {
    BooleanDataFlowType,
    IntegerDataFlowType,
    LongDataFlowType,
    NumberDataFlowType,
    StringDataFlowType,
    TimeStampDataFlowType
} from 'app/model/dataflow/dataflow-datatype';
import { isTruthy } from 'app/model/dataflow/dataflow-expression-utilities';
import { AzureCosmosDbV2Dataset } from 'app/model/dataset/azure-cosmos-db.v2.dataset';
import { stringIsNullOrEmpty } from 'app/shared/utils/string.util';
import { LinkedService } from 'app/model/linkedservice/linkedservice';
import { DataFlowAdapterUtils } from './adapter.util';
import { DataFlowLoggingService } from 'app/authoring/services/dataflow-logger.service';
import { FileBasedSettings, FILEMODE } from 'app/model/dataflow/dataset-settings/file-based-settings';
import { FormatDatasetV2 } from 'app/model/dataset/dataset.v2';
import { XmlDataset } from 'app/model/dataset/xml.dataset';
import { AzurePostgreSqlTableDataset } from 'app/model/dataset/azure-postgre-sql-table.dataset';
import { DelimitedTextSettings } from 'app/model/dataflow/dataset-settings/delimited-text-settings';
import { AzureMySqlTableDataset } from 'app/model/dataset/azure-mysql-table.dataset';
import { DataFlowExpression } from 'app/model/dataflow/dataflowexpression';
import { CdmSettings } from 'app/model/dataflow/dataset-settings/cdm-settings';
import { RestResourceDataset } from 'app/model/dataset/rest-resource.dataset';
import { DatasetUtils } from 'app/model/dataset/dataset.utils';
import { DynamicsEntityDataset } from 'app/model/dataset/dynamics-entity.dataset';
import { DynamicsCrmEntityDataset } from 'app/model/dataset/dynamics-crm.dataset';
import { SapOdpDataset } from 'app/model/dataset/sap-odp.dataset';
import { ReadMode, SapOdpSettings } from 'app/model/dataflow/dataset-settings/sap-odp-settings';
import { SqlBaseSettings } from 'app/model/dataflow/dataset-settings/sql-base-settings';
import { CommonDataServiceEntityDataset } from 'app/model/dataset/common-data-service.dataset';
import { DatasetFormatType } from 'app/model/dataflow/dataset-settings/dataflow-dataset-settings';
import {
    isEventHubsSource,
    isSapODataSource,
    isSapOdpSource,
    isSqlNativeCDCEnabled
} from 'app/model/dataflow/dataflow-utils';
import { SapOdpPartitionPredicate } from 'app/model/dataflow/sap-odp-partition-predicate';
import { PostgreSqlSettings } from 'app/model/dataflow/dataset-settings/postgre-sql-settings';
import { MysqlSettings } from 'app/model/dataflow/dataset-settings/mysql-settings';
import { OracleTableDataset } from 'app/model/dataset/oracle-table.dataset';
import { SnowflakeSettings } from 'app/model/dataflow/dataset-settings/snowflake-settings';
import { CassandraDataset } from 'app/model/dataset/cassandra.dataset';
import { CassandraAdapter } from './formatAdapters/cassandra.adapter';
import { CassandraSettings } from 'app/model/dataflow/dataset-settings/cassandra-settings';
import { EventHubsSettings } from 'app/model/dataflow/dataset-settings/event-hubs-settings';
import { getServiceInstance } from 'app/multi-contexts/modules/root/root.application';
import { DatasetRegistryService } from 'app/registries/dataset/dataset-registry.service';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(DataFlowSource),
    asynchronous: true
})
export class SourceAdapter extends BaseAsyncAdapter<DataFlowSource, IDataFlowSource> implements IDslScriptable {
    private _isCombinedScriptEnabled: boolean = !GlobalVariables.splitScriptEnabled;

    public sqlCdcTypeDataset(model: DataFlowSource): boolean {
        return (
            DatasetUtils.isSqlBasedDataset(model.sourceDataset) ||
            model.sourceDataset instanceof AzureSqlDWTableDataset ||
            model.sourceDataset instanceof AzurePostgreSqlTableDataset ||
            model.datasetSettings instanceof PostgreSqlSettings ||
            model.sourceDataset instanceof AzureMySqlTableDataset ||
            model.datasetSettings instanceof MysqlSettings ||
            (model.isInlineDataset && model.sourceLinkedService && model.datasetSettings instanceof SqlBaseSettings)
        );
    }

    public isSnowflakeCDCSource(model: DataFlowSource): boolean {
        return (model.isInlineDataset &&
                model.sourceLinkedService &&
                model.datasetSettings instanceof SnowflakeSettings) ||
                (!model.isInlineDataset && model.sourceDataset instanceof SnowflakeTableDataset);
    }

    public async getDslScript(model: DataFlowSource): Promise<string> {
        const partition = TransformationAdapter.getPartitionDslScript(model);
        const props: string[] = [DataFlowSchemaAdapterUtils.getOutputDefinitionDsl(model)];
        await DataFlowLoggingService.logColumnInfos(
            this.context.contextData,
            model.columns,
            model.name,
            Metamodel.modelFor(DataFlowSource).name,
            model.owner.name
        );
        if (model.isInlineDataset && model.datasetSettings) {
            const datasetFormatType = model.datasetSettings.getFormat();
            if (datasetFormatType === DatasetFormatType.Delimited || datasetFormatType === DatasetFormatType.Json || datasetFormatType === DatasetFormatType.CDM) {
                props.push(
                    `useSchema: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                        model.useSchema,
                        BooleanDataFlowType.TYPE
                    )}`
                );
            }
        }
        props.push(
            `allowSchemaDrift: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                model.allowSchemaDrift,
                BooleanDataFlowType.TYPE
            )}`
        );
        props.push(
            `validateSchema: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                model.validateSchema,
                BooleanDataFlowType.TYPE
            )}`
        );
        if (isTruthy(model.allowSchemaDrift) && isTruthy(model.inferDriftedColumnTypes)) {
            props.push(
                `inferDriftedColumnTypes: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                    model.inferDriftedColumnTypes,
                    BooleanDataFlowType.TYPE
                )}`
            );
        }
        if (model.sampling) {
            props.push(
                `limit: ${DataFlowExpressionUtils.wrapExpressionConfigValue(model.rowsLimit, IntegerDataFlowType.TYPE)}`
            );
        }

        if (model.isWorkspaceDatabase) {
            props.push(`store: 'synapse'`);
            props.push(
                `databaseType: ${DataFlowExpressionUtils.wrapExpressionConfigValue(model.workspaceDatabaseType)}`
            );
            props.push(`format: 'table'`);
            props.push(`database: ${DataFlowExpressionUtils.wrapExpressionConfigValue(model.workspaceDatabase)}`);
            props.push(`tableName: ${DataFlowExpressionUtils.wrapExpressionConfigValue(model.workspaceTable)}`);
        } else {
            if (
                (!model.isInlineDataset && model.sourceDataset && model.sourceDataset instanceof FormatDatasetV2) ||
                (model.isInlineDataset &&
                    model.datasetSettings &&
                    (model.datasetSettings instanceof FileBasedSettings ||
                        model.datasetSettings instanceof CdmSettings))
            ) {
                props.push(
                    `ignoreNoFilesFound: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                        model.ignoreNoFilesFound,
                        BooleanDataFlowType.TYPE
                    )}`
                );
            }
            if (
                model.isInlineDataset &&
                model.datasetSettings &&
                model.datasetSettings instanceof CdmSettings &&
                !isTruthy(model.multiLineRow)
            ) {
                props.push(
                    `multiLineRow: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                        model.multiLineRow,
                        BooleanDataFlowType.TYPE
                    )}`
                );
            }

            if (model.completionAction === CompletionAction.DeleteSource) {
                props.push(`purgeFiles: true`);
            } else if (model.completionAction === CompletionAction.MoveFile) {
                const fileList = model.moveFileList
                    .map((expression) => DataFlowExpressionUtils.wrapExpressionConfigValue(expression))
                    .join(',');
                props.push(`moveFiles: [${fileList}]`);
            }
            if (isTruthy(model.fileList)) {
                props.push(
                    `fileList: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                        model.fileList,
                        BooleanDataFlowType.TYPE
                    )}`
                );
            }
            if (
                (this.isSnowflakeCDCSource(model) || !isSapOdpSource(model)) && // We will handle Sap CDC separately
                isTruthy(model.enableCdc) &&
                !isEventHubsSource(model)
            ) {
                props.push(
                    `enableCdc: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                        model.enableCdc,
                        BooleanDataFlowType.TYPE
                    )}`
                );
                props.push(`mode: 'read'`);
            }
            if (isTruthy(model.enableNativeCdc)) {
                props.push(
                    `enableNativeCdc: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                        model.enableNativeCdc,
                        BooleanDataFlowType.TYPE
                    )}`
                );
            }
            if (isTruthy(model.enableNativeCdc) && isTruthy(model.netChanges)) {
                props.push(
                    `netChanges: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                        model.netChanges,
                        BooleanDataFlowType.TYPE
                    )}`
                );
            }
            if (this.isSnowflakeCDCSource(model) && isTruthy(model.enableNativeCdc) && model.systemColumns.expression) {
                props.push(
                    `systemColumns: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                        model.systemColumns,
                        BooleanDataFlowType.TYPE
                    )}`
                );
            }
            const dsRegistrySvc = await getServiceInstance<DatasetRegistryService>(this.context.adapterConfig.contextData, 'DatasetRegistryService');
            if (
                (isTruthy(model.enableCdc) || isTruthy(model.enableNativeCdc)) &&
                (DatasetUtils.isFileBasedTypeDataset(model.sourceDataset, { dsSvc: dsRegistrySvc }) ||
                    (model.isInlineDataset &&
                        model.sourceLinkedService &&
                        (model.datasetSettings instanceof FileBasedSettings ||
                            model.datasetSettings instanceof CdmSettings) &&
                        !(model.sourceDataset instanceof SapOdpDataset) &&
                        !(model.datasetSettings instanceof SapOdpSettings)) ||
                    this.sqlCdcTypeDataset(model) ||
                    this.isSnowflakeCDCSource(model))
            ) {
                props.push(`skipInitialLoad: ${isTruthy(model.enableSkipInitialLoad)}`);
            }
            if (model.waterMarkColumn.expression) {
                props.push(
                    `waterMarkColumn: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                        model.waterMarkColumn,
                        StringDataFlowType.TYPE
                    )}`
                );
            }
            if (model.rowUrl.expression) {
                props.push(`rowUrlColumn: ${DataFlowExpressionUtils.wrapExpressionConfigValue(model.rowUrl)}`);
            }
            if (model.modifiedAfter.expression) {
                props.push(
                    `modifiedAfter: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                        model.modifiedAfter,
                        TimeStampDataFlowType.TYPE
                    )}`
                );
            }
            if (model.modifiedBefore.expression) {
                props.push(
                    `modifiedBefore: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                        model.modifiedBefore,
                        TimeStampDataFlowType.TYPE
                    )}`
                );
            }
            if (model.partitionRootPath.expression) {
                props.push(
                    `partitionRootPath: ${DataFlowExpressionUtils.wrapExpressionConfigValue(model.partitionRootPath)}`
                );
            }
            if (model.isInlineDataset && model.sourceLinkedService && model.datasetSettings && !(model.sourceDataset instanceof CassandraDataset || model.datasetSettings instanceof CassandraSettings)) {
                const adapter = this.context.dataflowDSLAdapterContext.adapterFor(
                    Metamodel.modelOf(model.datasetSettings)
                );
                const settingProps = await adapter.toDSL(model.datasetSettings, true);
                props.push(...settingProps);
            }

            if (
                (model.isInlineDataset && model.datasetSettings instanceof DelimitedTextSettings) ||
                (!model.isInlineDataset && model.sourceDataset instanceof DelimitedTextDataset)
            ) {
                if (model.skipLineCount.expression && model.skipLineCount.expression !== '0') {
                    props.push(
                        `skipLines: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                            model.skipLineCount,
                            IntegerDataFlowType.TYPE
                        )}`
                    );
                }
                if (isTruthy(model.multiLineRow)) {
                    props.push(
                        `multiLineRow: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                            model.multiLineRow,
                            BooleanDataFlowType.TYPE
                        )}`
                    );
                }
                if (!stringIsNullOrEmpty(model.maxColumns.expression)) {
                    props.push(
                        `maxColumns: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                            model.maxColumns,
                            NumberDataFlowType.TYPE
                        )}`
                    );
                }
            }

            if (model.sourceDataset instanceof ParquetDataset) {
                props.push(`format: 'parquet'`);
            }

            if (!model.isInlineDataset && model.sourceDataset instanceof CassandraDataset) {
                if (model.useQuery) {
                    props.push(`format: 'query'`);
                    props.push(`query: ${DataFlowExpressionUtils.escapeAndWrapConfigValue(model.query)}`);
                } else {
                    props.push(`format: 'cassandratable'`);
                }

                props.push(`consistencyLevel: '${model.consistencyLevel}'`);
            } else if (model.isInlineDataset &&  model.datasetSettings instanceof CassandraSettings) {
                const adapter = new CassandraAdapter();
                const settingProps = await adapter.toDSL(model.datasetSettings as CassandraSettings, true);
                props.push(...settingProps);
            }

            if (
                model.executeSchemaQuery &&
                (isSapOdpSource(model) || isSqlNativeCDCEnabled(model) || isSapODataSource(model))
            ) {
                props.push(`executeSchemaQuery: ${String(Boolean(model.executeSchemaQuery))}`);
            }

            if (!model.isInlineDataset && model.sourceDataset) {
                if (model.sourceDataset instanceof RestResourceDataset) {
                    if (model.requestMethod.expression) {
                        props.push(
                            `httpMethod: ${DataFlowExpressionUtils.wrapExpressionConfigValue(model.requestMethod)}`
                        );
                    }
                    if (model.requestMethod.expression !== 'GET' && model.requestBody.expression) {
                        props.push(`body: ${DataFlowExpressionUtils.wrapExpressionConfigValue(model.requestBody)}`);
                    }
                    if (model.requestTimeout.expression) {
                        props.push(
                            `timeout: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                                model.requestTimeout,
                                IntegerDataFlowType.TYPE
                            )}`
                        );
                    }
                    if (model.requestInterval.expression) {
                        props.push(
                            `requestInterval: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                                model.requestInterval,
                                IntegerDataFlowType.TYPE
                            )}`
                        );
                    }
                    if (model.additionalHeaders && model.additionalHeaders.length) {
                        const headers = model.additionalHeaders
                            .filter((header) => header.name && header.value)
                            .map(
                                (header) =>
                                    `${DataFlowExpressionUtils.wrapExpressionConfigValue(
                                        header.paramName as DataFlowExpression
                                    )} -> ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                                        header.paramValue as DataFlowExpression
                                    )}`
                            )
                            .join(', ');
                        if (headers) {
                            props.push(`headers: [${headers}]`);
                        }
                    }
                    if (model.queryParameters && model.queryParameters.length) {
                        const queryParameters = model.queryParameters
                            .filter((p) => p.name)
                            .map(
                                (p) =>
                                    `${DataFlowExpressionUtils.wrapExpressionConfigValue(
                                        p.paramName as DataFlowExpression
                                    )} -> ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                                        p.paramValue as DataFlowExpression
                                    )}`
                            )
                            .join(', ');
                        if (queryParameters) {
                            props.push(`queryParameters: [${queryParameters}]`);
                        }
                    }

                    if (model.paginationRules && model.paginationRules.length) {
                        const paginationRules = model.paginationRules
                            .filter((rule) => rule.name && rule.value)
                            .map(
                                (rule) =>
                                    `${DataFlowExpressionUtils.wrapExpressionConfigValue(
                                        rule.paramName as DataFlowExpression
                                    )} -> ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                                        rule.paramValue as DataFlowExpression
                                    )}`
                            )
                            .join(', ');
                        if (paginationRules) {
                            props.push(`paginationRules: [${paginationRules}]`);
                        }
                    }

                    if (model.restDataFormat === 'json') {
                        const formatSettings = [`'type' -> 'json'`];
                        formatSettings.push(
                            `'documentForm' -> ${DataFlowExpressionUtils.wrapExpressionConfigValue(model.documentForm)}`
                        );
                        const formatSettingsInDsl = formatSettings.join(', ');
                        if (formatSettingsInDsl) {
                            props.push(`responseFormat: [${formatSettingsInDsl}]`);
                        }
                    } else if (model.restDataFormat === 'xml') {
                        const formatSettings = [`'type' -> 'xml'`];
                        if (model.nullValue) {
                            formatSettings.push(`'nullValue' -> '${model.nullValue}'`);
                        }
                        formatSettings.push(`'namespaces' -> ${model.namespaces}`);
                        if (model.namespaces) {
                            const namespacePrefixes = model.namespacePrefixes
                                .filter((namespacePrefix) => namespacePrefix.key && namespacePrefix.val)
                                .map(
                                    (namespacePrefix) =>
                                        `${DataFlowExpressionUtils.wrapExpressionConfigValue(
                                            model.createTemplatableExpression(
                                                StringDataFlowType.TYPE,
                                                namespacePrefix.key
                                            )
                                        )}->${DataFlowExpressionUtils.wrapExpressionConfigValue(
                                            model.createTemplatableExpression(
                                                StringDataFlowType.TYPE,
                                                namespacePrefix.val
                                            )
                                        )}`
                                )
                                .join(',');
                            if (namespacePrefixes.length > 0) {
                                formatSettings.push(`'namespacePrefixes' -> [${namespacePrefixes}]`);
                            }
                        }
                        const formatSettingsInDsl = formatSettings.join(', ');
                        if (formatSettingsInDsl) {
                            props.push(`responseFormat: [${formatSettingsInDsl}]`);
                        }
                    }
                }
                if (
                    model.sourceDataset instanceof DynamicsEntityDataset ||
                    model.sourceDataset instanceof DynamicsCrmEntityDataset ||
                    model.sourceDataset instanceof CommonDataServiceEntityDataset
                ) {
                    if (model.query.expression) {
                        props.push(`query: ${DataFlowExpressionUtils.escapeAndWrapConfigValue(model.query)}`);
                    }
                    if (model.timeout.expression) {
                        props.push(
                            `timeout: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                                model.timeout,
                                IntegerDataFlowType.TYPE
                            )}`
                        );
                    }
                }
                const dsRegistrySvc = await getServiceInstance<DatasetRegistryService>(this.context.adapterConfig.contextData, 'DatasetRegistryService');
                if (
                    DatasetUtils.isSqlBasedDataflowDataset(model.sourceDataset) ||
                    model.sourceDataset instanceof AzurePostgreSqlTableDataset ||
                    model.sourceDataset instanceof AzureMySqlTableDataset
                ) {
                    if (model.batchSize.expression) {
                        props.push(
                            `batchSize: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                                model.batchSize,
                                IntegerDataFlowType.TYPE
                            )}`
                        );
                    }
                    model.isolationLevel.expression = model.isolationLevel.expression || IsolationLevel.ReadUncommitted;
                    props.push(
                        `isolationLevel: ${DataFlowExpressionUtils.wrapExpressionConfigValue(model.isolationLevel)}`
                    );
                    if (
                        model.format === SQLSourceOptions.Procedure &&
                        DatasetUtils.isSqlBasedDataflowDataset(model.sourceDataset)
                    ) {
                        if (model.storedProcInputs.length) {
                            const inputs = model.storedProcInputs
                                .filter(
                                    (input) =>
                                        !stringIsNullOrEmpty(input.paramValue.expression) ||
                                        !stringIsNullOrEmpty(input.paramName.expression)
                                )
                                .map(
                                    (input) =>
                                        `${DataFlowExpressionUtils.wrapExpressionConfigValue(
                                            input.paramName as DataFlowExpression,
                                            StringDataFlowType.TYPE,
                                            true
                                        )} -> ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                                            input.paramValue as DataFlowExpression,
                                            StringDataFlowType.TYPE,
                                            true
                                        )}`
                                )
                                .join(',');
                            if (!!inputs) {
                                props.push(`inputs:[${inputs}]`);
                            }
                        }
                        props.push(
                            `procedureName: ${DataFlowExpressionUtils.wrapExpressionConfigValue(model.storedProcName)}`
                        );
                        if (!stringIsNullOrEmpty(model.schemaName.expression)) {
                            props.push(
                                `schemaName: ${DataFlowExpressionUtils.wrapExpressionConfigValue(model.schemaName)}`
                            );
                        }
                        props.push(
                            `resultSet: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                                model.storedProcResultSet,
                                BooleanDataFlowType.TYPE
                            )}`
                        );
                        props.push(`format: 'procedure'`);
                    } else {
                        if (model.isQueryMode({ dsRegistrySvc })) {
                            props.push(`query: ${DataFlowExpressionUtils.escapeAndWrapConfigValue(model.query)}`);
                            props.push(`format: 'query'`);
                        } else {
                            props.push(`format: 'table'`);
                        }
                    }
                    if (model.sourceDataset instanceof AzureSqlDWTableDataset) {
                        props.push(`staged: ${model.staged}`);
                    }
                }
                if (model.sourceDataset instanceof AzureDataExplorerDataset) {
                    if (!stringIsNullOrEmpty(model.timeout.expression)) {
                        props.push(
                            `timeout: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                                model.timeout,
                                IntegerDataFlowType.TYPE
                            )}`
                        );
                    }
                    if (model.isQueryMode({ dsRegistrySvc })) {
                        props.push(`query: ${DataFlowExpressionUtils.escapeAndWrapConfigValue(model.query)}`);
                        props.push(`format: 'query'`);
                    } else {
                        props.push(`format: 'table'`);
                    }
                }
                if (model.sourceDataset instanceof OracleTableDataset) {
                    const linkedService = model.sourceDataset.linkedService;
                    if (linkedService.integrationRuntime instanceof SelfHostedIntegrationRuntime) {
                        props.push('staged: true');
                    }
                    if (!stringIsNullOrEmpty(model.timeout.expression)) {
                        props.push(
                            `timeout: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                                model.timeout,
                                IntegerDataFlowType.TYPE
                            )}`
                        );
                    }
                    if (model.isQueryMode({ dsRegistrySvc })) {
                        props.push(`query: ${DataFlowExpressionUtils.escapeAndWrapConfigValue(model.query)}`);
                        props.push(`format: 'query'`);
                    } else {
                        props.push(`format: 'table'`);
                    }
                }
                if (model.sourceDataset instanceof SapOdpDataset) {
                    props.push('staged: true');
                    props.push(`readMode: ${DataFlowExpressionUtils.wrapExpressionConfigValue(model.sapReadMode)}`);
                    if (model.sapOdpLegacyMode && !model.sapReadMode.templateExpression) {
                        props.push(`enableCdc: ${model.sapReadMode.expression !== ReadMode.FULL_LOAD}`);
                    }
                    if (model.sapRunMode !== ReadBehavior.FULL_ON_EVERY_RUN) {
                        props.push(
                            `keys: ${DataFlowExpressionUtils.wrapExpressionArrayConfigValue(model.sapOdpKeyColumns)}`
                        );
                    }
                    props.push(`format: 'sapObject'`);
                    if (model.fixDecimalPrecision) {
                        props.push(`fixDecimalPrecision: ${model.fixDecimalPrecision}`);
                    }
                }
                if (model.sourceDataset instanceof SapODataDataset) {
                    props.push('staged: true');
                    props.push(`readMode: ${DataFlowExpressionUtils.wrapExpressionConfigValue(model.sapReadMode)}`);
                    if (model.sapRunMode !== ReadBehavior.FULL_ON_EVERY_RUN) {
                        props.push(
                            `keys: ${DataFlowExpressionUtils.wrapExpressionArrayConfigValue(model.sapOdpKeyColumns)}`
                        );
                    }
                    if (!stringIsNullOrEmpty(model.timeout.expression)) {
                        props.push(
                            `httpRequestTimeout: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                                model.timeout,
                                IntegerDataFlowType.TYPE
                            )}`
                        );
                    }
                    if (!stringIsNullOrEmpty(model.batchSize.expression)) {
                        props.push(
                            `batchSize: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                                model.batchSize,
                                IntegerDataFlowType.TYPE
                            )}`
                        );
                    }
                    props.push(`format: 'sapODataObject'`);
                }
                if (model.sourceDataset instanceof JsonDataset) {
                    props.push(
                        `documentForm: ${DataFlowExpressionUtils.wrapExpressionConfigValue(model.documentForm)}`
                    );
                    if (model.unquotedColumnNames) {
                        props.push(`unquotedColumnNames: true`);
                    }
                    if (model.hasComments) {
                        props.push(`hasComments: true`);
                    }
                    if (model.singleQuoted) {
                        props.push(`singleQuoted: true`);
                    }
                    if (model.backslashEscape) {
                        props.push(`backslashEscape: true`);
                    }
                }
                if (model.sourceDataset instanceof XmlDataset) {
                    if (model.validationMode.expression) {
                        props.push(
                            `validationMode: ${DataFlowExpressionUtils.wrapExpressionConfigValue(model.validationMode)}`
                        );
                    }
                    props.push(`namespaces: ${model.namespaces}`);
                    if (model.namespaces && model.namespacePrefixes && model.namespacePrefixes.length > 0) {
                        const namespacePrefixes = model.namespacePrefixes
                            .filter((namespacePrefix) => namespacePrefix.key && namespacePrefix.val)
                            .map(
                                (namespacePrefix) =>
                                    `${DataFlowExpressionUtils.wrapExpressionConfigValue(
                                        model.createTemplatableExpression(StringDataFlowType.TYPE, namespacePrefix.key)
                                    )}->${DataFlowExpressionUtils.wrapExpressionConfigValue(
                                        model.createTemplatableExpression(StringDataFlowType.TYPE, namespacePrefix.val)
                                    )}`
                            )
                            .join(',');
                        if (namespacePrefixes.length > 0) {
                            props.push(`namespacePrefixes: [${namespacePrefixes}]`);
                        }
                    }
                }
                if (model.sourceDataset instanceof AzureCosmosDbV2Dataset) {
                    if (!model.useQuery && isTruthy(model.enableChangeFeed)) {
                        props.push(
                            `enableChangeFeed: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                                model.enableChangeFeed,
                                BooleanDataFlowType.TYPE
                            )}`
                        );
                        props.push(
                            `changeFeedStartFromTheBeginning: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                                model.changeFeedStartFromBeginning,
                                BooleanDataFlowType.TYPE
                            )}`
                        );
                    }
                    if (model.useQuery) {
                        props.push(`query: ${DataFlowExpressionUtils.escapeAndWrapConfigValue(model.query)}`);
                        props.push(`format: 'documentQuery'`);
                    } else {
                        props.push(`format: 'document'`);
                    }
                    if (!stringIsNullOrEmpty(model.systemColumns.expression)) {
                        props.push(
                            `systemColumns: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                                model.systemColumns,
                                BooleanDataFlowType.TYPE
                            )}`
                        );
                    }
                    if (!stringIsNullOrEmpty(model.batchSize.expression)) {
                        props.push(
                            `batchSize: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                                model.batchSize,
                                IntegerDataFlowType.TYPE
                            )}`
                        );
                    }
                    if (!stringIsNullOrEmpty(model.throughput.expression)) {
                        props.push(
                            `throughput: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                                model.throughput,
                                IntegerDataFlowType.TYPE
                            )}`
                        );
                    }
                    if (model.preferredRegions != null && model.preferredRegions.length > 0) {
                        props.push(`preferredRegions: [${model.preferredRegions.map((v) => `'${v}'`).join(',')}]`);
                    }
                }
                if (model.sourceDataset instanceof SnowflakeTableDataset) {
                    if (model.useQuery) {
                        props.push(`query: ${DataFlowExpressionUtils.escapeAndWrapConfigValue(model.query)}`);
                        props.push(`format: 'query'`);
                    } else {
                        props.push(`format: 'table'`);
                    }
                }
            }
            if (model.dataTypeSettings) {
                if (!!model.dataTypeSettings.dateFormats && model.dataTypeSettings.dateFormats.length > 0) {
                    props.push(
                        `dateFormats: [${model.dataTypeSettings.dateFormats.map((v) => `'${v}'`).join(',')}]`
                    );
                }
                if (!!model.dataTypeSettings.timestampFormats && model.dataTypeSettings.timestampFormats.length > 0) {
                    props.push(
                        `timestampFormats: [${model.dataTypeSettings.timestampFormats
                            .map((v) => `'${DataFlowExpressionUtils.escapeSingleQuoteInDslString(v)}'`)
                            .join(',')}]`
                    );
                }
                if (
                    !!model.dataTypeSettings.preferredIntegralType &&
                    model.dataTypeSettings.preferredIntegralType !== 'none'
                ) {
                    props.push(`preferredIntegralType: '${model.dataTypeSettings.preferredIntegralType}'`);
                }
                if (
                    !!model.dataTypeSettings.preferredFractionalType &&
                    model.dataTypeSettings.preferredFractionalType !== 'none'
                ) {
                    props.push(`preferredFractionalType: '${model.dataTypeSettings.preferredFractionalType}'`);
                }
                if (!!model.dataTypeSettings.booleanTrueFormat && !!model.dataTypeSettings.booleanFalseFormat) {
                    props.push(
                        `booleanFormat: ['${model.dataTypeSettings.booleanTrueFormat}', '${model.dataTypeSettings.booleanFalseFormat}']`
                    );
                }
            }
            if (
                !model.isInlineDataset &&
                (DatasetUtils.isSqlBasedDataflowDataset(model.sourceDataset) ||
                    model.sourceDataset instanceof AzurePostgreSqlTableDataset ||
                    model.sourceDataset instanceof AzureMySqlTableDataset) &&
                model.partitionOption === 'Partitioning' &&
                model.partitionMethod === 'external'
            ) {
                if (model.sourceDataset instanceof AzurePostgreSqlTableDataset || model.useQueryPartition) {
                    const validExps = model.partitionPredicates.filter(
                        (exp) => exp.expression != null && exp.expression !== ''
                    );
                    if (model.partitionPredicates) {
                        const result = validExps
                            .map((s) => `'${DataFlowExpressionUtils.escapeSingleQuoteInDslString(s.expression)}'`)
                            .join(',');
                        props.push(`partitionPredicates: [${result}]`);
                    }
                } else {
                    if (model.partitionColumn) {
                        if (model.partitionColumn.dynamicPartitionColumn?.templateExpression) {
                            props.push(
                                `partitionColumn: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                                    model.partitionColumn.dynamicPartitionColumn,
                                    StringDataFlowType.TYPE
                                )}`
                            );
                        } else {
                            props.push(`partitionColumn: '${model.partitionColumn.serializableName}'`);
                        }
                    }
                }
            }
            if (
                (model.sourceDataset instanceof SapOdpDataset || model.datasetSettings instanceof SapOdpSettings) &&
                model.partitionOption === 'Partitioning' &&
                model.partitionMethod === 'external'
            ) {
                if (model.sapOdpPartitionPredicates.templateExpression) {
                    props.push(
                        `partitionPredicates: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                            model.sapOdpPartitionPredicates
                        )}`
                    );
                } else {
                    const EOL = (n: number = 1) => `\n${'\t'.repeat(n)}`;
                    const predicates: string[] = model.sapOdpPartitionPredicates.expressions.map(
                        (conditions) =>
                            `[${EOL(3)}${conditions
                                .map(
                                    (condition) =>
                                        `[${EOL(4)}${SapOdpPartitionPredicate.toDSL(condition).join(`,${EOL(4)}`)}${EOL(
                                            3
                                        )}]`
                                )
                                .join(`,${EOL(3)}`)}${EOL(2)}]`
                    );
                    props.push(`partitionPredicates: [${EOL(2)}${predicates.join(`,${EOL(2)}`)}${EOL()}]`);
                }
            }
            if (model.datasetSettings instanceof EventHubsSettings && model.partitionOption === 'Partitioning' && model.partitionMethod === 'external') {
                if (model.maxRecordsPerPartition?.templateExpression) {
                    props.push(
                        `maxRecordsPerPartition: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                            model.maxRecordsPerPartition,
                            LongDataFlowType.TYPE
                        )}`
                    );
                } else {
                    props.push(`maxRecordsPerPartition: ${model.maxRecordsPerPartition.expression}L`);
                }
                props.push(`numPartitions: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                    model.numberOfPartitions,
                    IntegerDataFlowType.TYPE
                )}`);
            }
            if (
                !model.isInlineDataset ||
                !model.datasetSettings ||
                (model.datasetSettings instanceof FileBasedSettings &&
                    model.datasetSettings.fileMode === FILEMODE.WILDCARD)
            ) {
                const paths = model.wildcardPaths
                    .filter((path) => path.expression)
                    .map((str) => DataFlowExpressionUtils.wrapExpressionConfigValue(str))
                    .join(',');
                if (paths) {
                    props.push(`wildcardPaths:[${paths}]`);
                }
            }
        }
        if (partition) {
            props.push(partition);
        }

        // Fabric migration: supplement format properties from source
        if (this.context.adapterConfig.convertToFabric) {
            const dataset = model.sourceDataset;
            const linkedService = model.isInlineDataset ? model.sourceLinkedService : dataset?.linkedService;
            const adapter = dataset
                ? DataFlowAdapterUtils.getFormatAdapterForDataset(this.context, dataset)
                : (model.datasetSettings ? this.context.dataflowDSLAdapterContext.adapterFor(Metamodel.modelOf(model.datasetSettings)) : undefined);
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (adapter as any)?.supplementPropsForFabricMigration?.(dataset, linkedService, props, true, model.isInlineDataset, model.datasetSettings, model.format, model.wildcardPaths);
        }

        const knownProps = props.filter((prop) => !!prop).join(',\n\t');
        const unknownProps = DataFlowAdapterUtils.getUnknownProps(model, knownProps);
        const transformationData = [knownProps, unknownProps].filter((c) => !!c).join(',\n\t');
        const outputStream = model.outputStream;
        return `source(${transformationData}) ${FLOW} ${outputStream ? outputStream.qualifiedName : ''}`;
    }

    protected async _toResource(model: DataFlowSource): Promise<IDataFlowSource> {
        const resource: IDataFlowSource = {
            dataset: model.sourceDataset
                ? (ReferenceAdapter.toResource(model.sourceDataset, this.context) as Model.DatasetReference)
                : undefined,
            linkedService:
                model.isInlineDataset && model.sourceLinkedService
                    ? (ReferenceAdapter.toResource(
                        model.sourceLinkedService,
                        this.context
                    ) as Model.LinkedServiceReference)
                    : undefined,
            schemaLinkedService:
                model.isInlineDataset && model.schemaLinkedService && model.includeSchemaLinkedService
                    ? (ReferenceAdapter.toResource(
                        model.schemaLinkedService,
                        this.context
                    ) as Model.LinkedServiceReference)
                    : undefined,
            name: model.name,
            description: model.description
        };

        if (__IS_FABRIC_MDF__ && this._isMdfFabricArtifactSource(model)) {
            await this._applyMdfArtifactSourceShape(model, resource);
        }

        if (!this._isCombinedScriptEnabled) {
            resource.script = await this.getDslScript(model);
        }

        return resource;
    }

    protected async _toModel(resource: IDataFlowSource, dataflowSource?: DataFlowSource): Promise<DataFlowSource> {
        if (!dataflowSource) {
            dataflowSource = Metamodel.modelFor(DataFlowSource).create<DataFlowSource>(resource.name);
        }
        if (resource.dataset) {
            dataflowSource.sourceDataset = ReferenceAdapter.toProxyModel<Dataset>(
                Metamodel.modelFor(Dataset),
                resource.dataset, this.context
            );
        }
        if (resource.linkedService) {
            dataflowSource.sourceLinkedService = ReferenceAdapter.toProxyModel<LinkedService>(
                Metamodel.modelFor(LinkedService),
                resource.linkedService, this.context
            );
        }
        if (resource.schemaLinkedService) {
            dataflowSource.schemaLinkedService = ReferenceAdapter.toProxyModel<LinkedService>(
                Metamodel.modelFor(LinkedService),
                resource.schemaLinkedService, this.context
            );
        }
        dataflowSource.name = resource.name;
        dataflowSource.description = resource.description;
        dataflowSource.restDataFormat = undefined;
        dataflowSource.paginationRules = [];

        if (__IS_FABRIC_MDF__) {
            this._restoreMdfArtifactSourceModel(resource, dataflowSource);
        }

        return dataflowSource;
    }

    protected override async _toAzureResource(tridentResource: ITridentResource): Promise<never> {
        throw `Artifact type '${tridentResource.artifactType}' does not implement _toAzureResource method.`;
    }

    // ── MDF Fabric artifact source helpers ──────────────────────────────────

    /** Returns true when the MDF source is a workspace-built-in Lakehouse or Warehouse. */
    private _isMdfFabricArtifactSource(model: DataFlowSource): boolean {
        return model.isInlineDataset &&
            model.sourceLinkedService?.isWorkspaceBuiltIn === true &&
            (model.sourceLinkedService instanceof LakehouseLinkedService ||
                model.sourceLinkedService instanceof DataWarehouseLinkedService);
    }

    /**
     * Shapes the outgoing source JSON for Fabric Lakehouse/Warehouse inline-dataset sources.
     *
     *  - DMTS credential selected: outer `typeProperties` carry artifactId/workspaceId
     *    (+ `endpoint` for Warehouse) and `linkedService` is rewritten to point at the
     *    credential (becomes `externalReferences.connection`).
     *  - "None" (connectionless): the artifact's `type`/`typeProperties` are emitted inline
     *    (matching the connection-based nodes) and `linkedService` is cleared so no
     *    `externalReferences.connection` is emitted.
     *
     * In both cases `schemaLinkedService` is cleared: the schema is already known from the
     * artifact connection itself and emitting it would produce an unwanted `schemaExternalReferences`
     * entry in the MDF source JSON.
     */
    private async _applyMdfArtifactSourceShape(
        model: DataFlowSource,
        resource: IDataFlowSource
    ): Promise<void> {
        // The artifact LS may store the artifact ID in either `artifactId` or `objectId`
        const artifactLs = model.sourceLinkedService as { workspaceId?: string; artifactId?: string; objectId?: string; endpoint?: string };

        if (model.sourceDmtsCredentialLinkedService) {
            this._applyDmtsCredentialShape(model, resource, artifactLs);
        } else {
            await this._applyConnectionlessShape(model, resource);
        }

        // Schema is derived from the artifact itself — suppress the reference so MDF
        // does not emit a spurious `schemaExternalReferences` block.
        resource.schemaLinkedService = undefined;
    }
    /**
     * MDF Lakehouse/Warehouse with DMTS credential: Writes typeProperties (artifactId, workspaceId,
     * endpoint for Warehouse) and rewrites linkedService to point to the DMTS credential reference.
     * Also saves artifactName so it can be restored when reopening the dataflow.
     *
     * Saved JSON structure:
     * ```json
     * {
     *   "typeProperties": { "artifactId": "...", "workspaceId": "...", "artifactName": "..." },
     *   "externalReferences": { "connection": "..." }
     * }
     * ```
     */
    private _applyDmtsCredentialShape(
        model: DataFlowSource,
        resource: IDataFlowSource,
        artifactLs: { workspaceId?: string; artifactId?: string; objectId?: string; endpoint?: string }
    ): void {
        // Use artifactId if available, otherwise fall back to objectId
        const artifactId = artifactLs.artifactId ?? artifactLs.objectId;
        if (artifactId) {
            const isWarehouse = model.sourceLinkedService instanceof DataWarehouseLinkedService;
            // Set type as sibling of typeProperties (not inside it)
            resource.type = isWarehouse ? 'DataWarehouse' : 'Lakehouse';
            resource.typeProperties = {
                ...(isWarehouse && artifactLs.endpoint
                    ? { endpoint: artifactLs.endpoint }
                    : {}),
                artifactId,
                workspaceId: artifactLs.workspaceId ?? model.sourceLinkedService.workspaceObjectId,
            };
        }
        const linkedServiceRef = ReferenceAdapter.toResource(
            model.sourceDmtsCredentialLinkedService,
            this.context
        ) as Model.LinkedServiceReference;
        resource.linkedService = linkedServiceRef;
    }

    /**
     * Connectionless ("None") case: emits the artifact's `type`/`typeProperties` inline
     * (matching the connection-based nodes) instead of wrapping them in a `connectionSettings`
     * block. Strips extra typeProperty fields the LS adapter may have written so only the
     * fields MDF expects are present. The backend converter reconstructs the connection
     * settings from these inline fields.
     */
    private async _applyConnectionlessShape(
        model: DataFlowSource,
        resource: IDataFlowSource
    ): Promise<void> {
        const linkedServiceAdapter = this.context.asyncAdapterFor(Metamodel.modelOf(model.sourceLinkedService));
        const inlinedLinkedService: Model.LinkedServiceResource = await linkedServiceAdapter.toResource(
            model.sourceLinkedService
        );

        if (inlinedLinkedService?.properties) {
            this._stripExtraTypeProperties(inlinedLinkedService, model.sourceLinkedService instanceof DataWarehouseLinkedService);
        }

        resource.type = inlinedLinkedService.properties?.type;
        resource.typeProperties = inlinedLinkedService.properties?.typeProperties as Record<string, unknown> | undefined;
        resource.linkedService = undefined;
    }

    /**
     * Removes typeProperty keys that MDF does not expect.
     * Warehouse keeps: workspaceId, artifactId, endpoint.
     * Lakehouse keeps: workspaceId, artifactId.
     */
    private _stripExtraTypeProperties(
        linkedServiceResource: Model.LinkedServiceResource,
        isWarehouse: boolean
    ): void {
        const tp = linkedServiceResource.properties?.typeProperties as Record<string, unknown> | undefined;
        if (!tp) { return; }
        const allowedKeys: ReadonlySet<string> = new Set(
            isWarehouse ? ['workspaceId', 'artifactId', 'endpoint'] : ['workspaceId', 'artifactId']
        );
        for (const key of Object.keys(tp)) {
            if (!allowedKeys.has(key)) {
                Reflect.deleteProperty(tp, key);
            }
        }
    }

    /**
     * Reconstructs `sourceLinkedService` and `sourceDmtsCredentialLinkedService` when
     * loading a source back from JSON. The artifact's `type`/`typeProperties` are inlined on the
     * node; the artifact LS model is rebuilt from them and a DMTS credential, when present, is
     * resolved from `linkedService`/`linkedServiceName`.
     */
    private _restoreMdfArtifactSourceModel(
        resource: IDataFlowSource,
        dataflowSource: DataFlowSource
    ): void {
        if (resource.typeProperties) {
            // typeProperties contains artifactId/workspaceId - create artifact LS from it
            this._restoreFromTypeProperties(resource, dataflowSource);
        }
        // If typeProperties is absent, leave sourceLinkedService as-is
        // (it was already set from the standard linkedService reference path above)
    }

    /**
     * Restores the artifact LS (and optional DMTS credential) from the inline `typeProperties`
     * shape (artifactId/workspaceId + `endpoint` for Warehouse). Handles both the connectionless
     * case (no credential) and the DMTS-credential case, where the credential reference is in
     * `linkedService` or `linkedServiceName` (the latter parsed from `externalReferences.connection`
     * during DSL parsing).
     *
     * Key restore steps:
     * 1. Determine if Warehouse or Lakehouse (prefer top-level `type`, fall back to endpoint presence)
     * 2. Create artifact LS model with correct tridentArtifactType
     * 3. Restore DMTS credential from `linkedService`/`linkedServiceName` when present
     * 4. Use saved `artifactName` for display name (fallback to 'artifact')
     */
    private _restoreFromTypeProperties(
        resource: IDataFlowSource,
        dataflowSource: DataFlowSource
    ): void {
        const tp = resource.typeProperties as { workspaceId?: string; artifactId?: string; endpoint?: string; artifactName?: string };
        
        // Determine artifact type: prefer the top-level `type` (always emitted); fall back to
        // endpoint presence only when `type` is absent (Warehouse carries an endpoint, Lakehouse does not).
        const isWarehouse = resource.type != null
            ? resource.type === 'DataWarehouse' || resource.type === 'Warehouse'
            : !!tp.endpoint;
        // Use saved artifact name or fallback to 'artifact'
        const artifactName = tp.artifactName ?? 'artifact';
        const artifactModel = isWarehouse
            ? Metamodel.modelFor(DataWarehouseLinkedService)
            : Metamodel.modelFor(LakehouseLinkedService);
        // Resolve the workspace artifact as a store-backed proxy keyed by its objectId (artifactId) - the
        // SAME identity copy job uses (getResourceIdentifier('linkedService', artifactId) => "linkedservice$$<id>").
        // `sourceLinkedService` is a `reference: true` property, so the model service's fetch-time walker
        // substitutes this marked proxy with the real store entity (the SAME instance as the artifact-list
        // option) - no component-side objectId reconciliation needed. A typed proxy keeps
        // `instanceof Lakehouse/DataWarehouseLinkedService` working before substitution. Fall back to a
        // detached model only when there is no artifactId to resolve by.
        const lsModel: LinkedService = tp.artifactId
            ? ReferenceAdapter.toProxyModel<LinkedService>(artifactModel, { referenceName: tp.artifactId }, this.context)
            : artifactModel.create<LinkedService>(artifactName);
        lsModel.name = artifactName;

        const ls = lsModel as unknown as { workspaceId?: string; artifactId?: string; endpoint?: string };

        // Set tridentArtifactType so isWorkspaceBuiltIn returns true
        lsModel.tridentArtifactType = isWarehouse ? 'DataWarehouse' : 'Lakehouse';

        if (tp.workspaceId) {
            ls.workspaceId = tp.workspaceId;
            lsModel.workspaceObjectId = tp.workspaceId;
        }
        if (tp.artifactId) {
            ls.artifactId = tp.artifactId;
            lsModel.objectId = tp.artifactId;
        }
        if (tp.endpoint) {
            ls.endpoint = tp.endpoint;
        }

        // The DMTS credential reference can be in:
        // 1. `resource.linkedService` (direct reference shape)
        // 2. `resource.linkedServiceName` (parsed from externalReferences.connection during DSL parsing)
        const linkedServiceName = (resource as { linkedServiceName?: { type?: string; referenceName?: string } }).linkedServiceName;

        if (resource.linkedService) {
            const dmtsProxy = ReferenceAdapter.toProxyModel<LinkedService>(
                Metamodel.modelFor(LinkedService),
                resource.linkedService,
                this.context
            );
            if (dmtsProxy) {
                const ref = resource.linkedService as { referenceName?: string };
                if (ref.referenceName && !dmtsProxy.objectId) {
                    dmtsProxy.objectId = ref.referenceName;
                }
            }
            dataflowSource.sourceDmtsCredentialLinkedService = dmtsProxy;
        } else if (linkedServiceName?.referenceName) {
            // MDF saves DMTS credential as externalReferences.connection, which gets parsed to linkedServiceName.
            // Resolve as a store-backed proxy (same as copy activity's dataset adapter) so it is the same
            // instance as the connection-list option and needs no component-side objectId reconciliation.
            const dmtsProxy = ReferenceAdapter.toProxyModel<LinkedService>(
                Metamodel.modelFor(LinkedService),
                linkedServiceName,
                this.context
            );
            if (dmtsProxy && !dmtsProxy.objectId) {
                dmtsProxy.objectId = linkedServiceName.referenceName;
            }
            dataflowSource.sourceDmtsCredentialLinkedService = dmtsProxy;
        } else {
            // Connectionless: no credential reference present. Clear any stale value so reusing an
            // existing DataFlowSource model in `_toModel` doesn't retain a previously selected credential.
            dataflowSource.sourceDmtsCredentialLinkedService = undefined;
        }
        
        dataflowSource.sourceLinkedService = lsModel;
    }

}
